import "../../styles/voice-animation.css";

import React, { useState, useRef } from "react";
import axios from "../../utils/axios.tsx";
import Tooltip from "@mui/material/Tooltip";

import IconButton from "@mui/material/IconButton";
import GraphicEqIcon from "@mui/icons-material/GraphicEq";
import { searchFieldActions } from "../../store/search/searchField.tsx";
import { useDispatch } from "react-redux";
import { voiceButtonSx } from "../voice/voiceButtonSx.ts";
import { Trans } from "@lingui/react/macro";
import ComposerActionButton from "./ComposerActionButton.tsx";
import { MicIcon } from "../icons/ComposerIcons.tsx";

const SILENCE_THRESHOLD = 0.01; // volume level
const SILENCE_DURATION = 1500; // ms
const MAX_RECORDING_TIME = 25000; // ~50 words

type Props = {
  disabled?: boolean;
  /** Renders the composer's inline action-row form: small, labelled, pill-shaped. */
  compact?: boolean;
};

const VoiceRecorder: React.FC<Props> = ({ disabled, compact }: Props) => {
  const [recording, setRecording] = useState(false);
  const dispatch = useDispatch();

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<BlobPart[]>([]);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const silenceTimerRef = useRef<number | null>(null);
  const maxTimerRef = useRef<number | null>(null);
  const recordingRef = useRef(false);

  const startRecording = async () => {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

    // MediaRecorder
    const mediaRecorder = new MediaRecorder(stream);
    mediaRecorderRef.current = mediaRecorder;
    audioChunksRef.current = [];

    mediaRecorder.ondataavailable = (event) => {
      audioChunksRef.current.push(event.data);
    };

    mediaRecorder.onstop = async () => {
      // reset audio refs
      cleanupAudioAnalysis();

      const audioBlob = new Blob(audioChunksRef.current, {
        type: "audio/webm",
      });
      const formData = new FormData();
      formData.append("file", audioBlob, "audio.webm");

      try {
        const response = await axios.post("/v1/speech-to-text/", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });

        // submit the text content
        dispatch(searchFieldActions.setTextAndSubmit(response.data.text));
      } catch (err) {
        console.error(err);
      }
    };

    mediaRecorder.start();
    recordingRef.current = true;
    setRecording(true);

    setupSilenceDetection(stream);

    // Max duration cutoff
    maxTimerRef.current = window.setTimeout(stopRecording, MAX_RECORDING_TIME);
  };

  const stopRecording = () => {
    if (!recordingRef.current) return;
    recordingRef.current = false;
    mediaRecorderRef.current?.stop();
    setRecording(false);
  };

  // 🎧 Silence detection
  const setupSilenceDetection = (stream: MediaStream) => {
    const audioContext = new AudioContext();
    const analyser = audioContext.createAnalyser();
    const source = audioContext.createMediaStreamSource(stream);

    analyser.fftSize = 2048;
    source.connect(analyser);

    audioContextRef.current = audioContext;
    analyserRef.current = analyser;

    const data = new Uint8Array(analyser.fftSize);

    let silenceStart: number | null = null; // track when silence started

    const checkVolume = () => {
      analyser.getByteTimeDomainData(data);

      let sum = 0;
      for (let i = 0; i < data.length; i++) {
        const val = (data[i] - 128) / 128;
        sum += val * val;
      }

      const volume = Math.sqrt(sum / data.length);
      const now = Date.now();

      // ✅ continuous silence detection
      if (volume < SILENCE_THRESHOLD) {
        if (!silenceStart)
          silenceStart = now; // start of silence
        else if (now - silenceStart > SILENCE_DURATION) {
          stopRecording();
          return; // stop immediately
        }
      } else {
        silenceStart = null; // reset if user speaks
      }

      if (recordingRef.current) {
        requestAnimationFrame(checkVolume);
      }
    };

    checkVolume();
  };

  const cleanupAudioAnalysis = () => {
    audioContextRef.current?.close();
    analyserRef.current = null;

    if (silenceTimerRef.current) clearTimeout(silenceTimerRef.current);
    if (maxTimerRef.current) clearTimeout(maxTimerRef.current);
  };

  if (compact) {
    return (
      <Tooltip title={recording ? "Stop Recording" : "Use Voice Mode"}>
        {/* span wrapper needed when button can be disabled */}
        <span>
          <ComposerActionButton
            onClick={recording ? stopRecording : startRecording}
            disabled={disabled}
            icon={<MicIcon className={recording ? "recordingAnimation" : ""} />}
            label={
              <Trans context="Composer action that dictates a question">
                Voice
              </Trans>
            }
          />
        </span>
      </Tooltip>
    );
  }

  return (
    <Tooltip title={recording ? "Stop Recording" : "Use Voice Mode"}>
      {/* span wrapper needed when button can be disabled */}
      <span>
        <IconButton
          onClick={recording ? stopRecording : startRecording}
          disabled={disabled}
          sx={voiceButtonSx}
        >
          <GraphicEqIcon
            fontSize="small"
            className={recording ? "recordingAnimation" : ""}
          />
        </IconButton>
      </span>
    </Tooltip>
  );
};

export default VoiceRecorder;
